// Shrinks an image in the browser before upload, so phones don't send 5–15 MB originals.
// Best-effort only: the server normalises every image anyway (incl. formats the browser can't decode,
// such as HEIC in Chrome), so on any failure the original file is returned unchanged.
export const MAX_IMAGE_DIMENSION = 2048;
const JPEG_QUALITY = 0.9; // server re-encodes at 80; keep the browser step near-lossless
const SKIP_BELOW_BYTES = 1.5 * 1024 * 1024;

const replaceExtension = (name: string, ext: string) => {
  const dot = name.lastIndexOf('.');
  return `${dot > 0 ? name.slice(0, dot) : name || 'image'}${ext}`;
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });

export const prepareImageForUpload = async (file: File): Promise<File> => {
  // Vector and animated formats are passed through untouched.
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') {
    return file;
  }

  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
    return file;
  }

  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const { width, height } = bitmap;
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(width, height));
    if (scale === 1 && file.size <= SKIP_BELOW_BYTES) {
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }

    // PNG/WebP may carry transparency → keep a lossless format; everything else becomes JPEG.
    const keepsAlpha = file.type === 'image/png' || file.type === 'image/webp';
    if (!keepsAlpha) {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const type = keepsAlpha ? 'image/png' : 'image/jpeg';
    const blob = await canvasToBlob(canvas, type, keepsAlpha ? undefined : JPEG_QUALITY);
    if (!blob || blob.size >= file.size) {
      return file;
    }

    return new File([blob], replaceExtension(file.name, keepsAlpha ? '.png' : '.jpg'), { type });
  } catch {
    // Format the browser can't decode (e.g. HEIC in Chrome): let the server handle it.
    return file;
  } finally {
    bitmap?.close();
  }
};
